import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import TestPayPalButton from "@/components/TestPayPalButton";
import {
  Check, X, Sparkles, ShieldCheck, Loader2, Crown, Gift,
} from "lucide-react";

const PLAN_ICONS = { free: Gift, basic: ShieldCheck, pro: Crown };

function formatEuroInt(v) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR", minimumFractionDigits: 0,
  }).format(v || 0);
}

export default function Plans() {
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);
  const [billing, setBilling] = useState("yearly"); // default consigliato

  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([
          api.get("/plans"),
          api.get("/me/subscription"),
        ]);
        setPlans(p.data.plans);
        setSubscription(s.data);
        if (s.data.billing_cycle) setBilling(s.data.billing_cycle);
      } catch {
        toast.error("Errore nel caricamento piani");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const activate = async (planId) => {
    if (planId === "free") {
      toast.info("Sei già sul piano Free — inizia creando un cliente!");
      nav("/");
      return;
    }
    setActivating(planId);
    try {
      const res = await api.post("/subscriptions/upgrade", {
        plan: planId, billing_cycle: billing,
      });
      setSubscription(res.data);
      await refreshUser();
      toast.success(
        `Piano ${planId.toUpperCase()} attivato (${billing === "yearly" ? "annuale" : "mensile"})`
      );
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'attivazione");
    } finally {
      setActivating(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Confermi la cancellazione dell'abbonamento? Manterrai l'accesso fino alla fine del periodo.")) return;
    try {
      const res = await api.post("/subscriptions/cancel");
      setSubscription(res.data);
      await refreshUser();
      toast.success("Abbonamento cancellato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
        </div>
      </Layout>
    );
  }

  const currentPlan = subscription?.plan || "free";

  return (
    <Layout>
      <div className="mb-10 animate-fade-up">
        <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">Abbonamenti</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] mb-3">
          Scegli il tuo piano
        </h1>
        <p className="text-[#737373] max-w-2xl">
          Inizia gratis con 5 analisi, o sblocca subito analisi mensili e strumenti professionali.
          Cancella quando vuoi, nessun vincolo.
        </p>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center justify-center mb-10" data-testid="billing-toggle">
        <div className="inline-flex items-center bg-white border border-[#E5E5E0] rounded-full p-1 shadow-sm">
          <button
            type="button"
            onClick={() => setBilling("monthly")}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
              billing === "monthly"
                ? "bg-[#1A1A1A] text-white"
                : "text-[#737373] hover:text-[#1A1A1A]"
            }`}
            data-testid="billing-monthly"
          >
            Mensile
          </button>
          <button
            type="button"
            onClick={() => setBilling("yearly")}
            className={`relative px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
              billing === "yearly"
                ? "bg-[#C5A059] text-white"
                : "text-[#737373] hover:text-[#1A1A1A]"
            }`}
            data-testid="billing-yearly"
          >
            Annuale
            <span className="ml-2 inline-block bg-[#EAE5DF] text-[#1A1A1A] text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full">
              Risparmia
            </span>
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6" data-testid="plans-grid">
        {plans.map((p) => {
          const Icon = PLAN_ICONS[p.id] || Sparkles;
          const isCurrent = p.id === currentPlan;
          const isRecommended = p.recommended;
          const isFree = p.id === "free";
          const isYearly = billing === "yearly" && !isFree;
          const displayedPrice = isFree
            ? 0
            : isYearly ? p.yearly_price_per_month_eur : p.monthly_price_eur;
          const savings = isYearly ? p.yearly_savings_eur : 0;
          const yearlyTotal = p.yearly_total_eur;

          return (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className={`relative rounded-2xl p-8 flex flex-col ${
                isRecommended
                  ? "bg-[#1A1A1A] text-white border border-[#C5A059] shadow-[0_10px_40px_rgba(197,160,89,0.18)]"
                  : "bg-white border border-[#E5E5E0]"
              }`}
            >
              {isRecommended && (
                <div className="absolute -top-3 left-8 bg-[#C5A059] text-white text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full font-semibold">
                  Consigliato
                </div>
              )}
              {isYearly && !isRecommended && !isFree && (
                <div className="absolute -top-3 left-8 bg-[#EAE5DF] text-[#1A1A1A] text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full font-semibold border border-[#D6CFBE]">
                  Più conveniente
                </div>
              )}
              {isCurrent && (
                <div className={`absolute -top-3 right-8 text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full font-semibold ${
                  isRecommended ? "bg-white text-[#1A1A1A]" : "bg-[#1A1A1A] text-white"
                }`}>
                  Piano attivo
                </div>
              )}

              <Icon className="w-7 h-7 mb-5 text-[#C5A059]" />

              <h3 className={`font-serif-display text-3xl mb-1 ${isRecommended ? "text-white" : "text-[#1A1A1A]"}`}>
                {p.label}
              </h3>
              <p className={`text-sm mb-6 ${isRecommended ? "text-white/60" : "text-[#737373]"}`}>
                {isFree && "Per provare la piattaforma"}
                {p.id === "basic" && "Per saloni in crescita"}
                {p.id === "pro" && "Per studi professionali e saloni orientati alla crescita"}
              </p>

              <div className="mb-6">
                {isFree ? (
                  <span className={`font-serif-display text-5xl ${isRecommended ? "text-white" : "text-[#1A1A1A]"}`}>
                    Gratis
                  </span>
                ) : (
                  <>
                    <div className="flex items-baseline gap-1" data-testid={`plan-price-${p.id}`}>
                      <span className={`font-serif-display text-5xl ${isRecommended ? "text-white" : "text-[#1A1A1A]"}`}>
                        {formatEuroInt(displayedPrice)}
                      </span>
                      <span className={`text-sm ${isRecommended ? "text-white/60" : "text-[#737373]"}`}>
                        /mese
                      </span>
                    </div>
                    {isYearly && yearlyTotal > 0 && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className={`text-xs ${isRecommended ? "text-white/60" : "text-[#737373]"}`}>
                          Fatturato annualmente ({formatEuroInt(yearlyTotal)})
                        </span>
                        {savings > 0 && (
                          <span
                            className="inline-block bg-[#C5A059] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                            data-testid={`plan-savings-${p.id}`}
                          >
                            Risparmi {formatEuroInt(savings)}
                          </span>
                        )}
                      </div>
                    )}
                    {!isYearly && (
                      <p className={`mt-2 text-xs ${isRecommended ? "text-white/50" : "text-[#737373]"}`}>
                        Fatturato mensilmente
                      </p>
                    )}
                  </>
                )}
              </div>

              <ul className="space-y-3 mb-5 flex-1">
                {p.features.map((f, i) => {
                  const isExcluded = f.startsWith("✗ ") || f.startsWith("❌ ");
                  const text = isExcluded ? f.replace(/^[✗❌]\s+/, "") : f;
                  const IconComp = isExcluded ? X : Check;
                  const iconColor = isExcluded
                    ? (isRecommended ? "text-white/40" : "text-[#A3A3A3]")
                    : "text-[#C5A059]";
                  const textColor = isExcluded
                    ? (isRecommended ? "text-white/50" : "text-[#A3A3A3]")
                    : (isRecommended ? "text-white/90" : "text-[#1A1A1A]");
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <IconComp className={`w-4 h-4 shrink-0 mt-1 ${iconColor}`} />
                      <span className={`text-sm ${textColor} ${isExcluded ? "line-through decoration-1 decoration-[#D4D4D4]" : ""}`}>
                        {text}
                      </span>
                    </li>
                  );
                })}
              </ul>

              {p.tagline && (
                <p
                  data-testid={`plan-tagline-${p.id}`}
                  className={`text-xs italic mb-6 leading-relaxed ${
                    isRecommended ? "text-white/70" : "text-[#737373]"
                  }`}
                >
                  {p.tagline}
                </p>
              )}

              {/* CTA area — free keeps the direct activate flow, paid plans
                  require PayPal payment before any plan change. */}
              {isFree || isCurrent ? (
                <button
                  disabled={isCurrent || activating === p.id}
                  onClick={() => activate(p.id)}
                  data-testid={`plan-cta-${p.id}`}
                  className={`h-12 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    isRecommended
                      ? "bg-[#C5A059] hover:bg-white hover:text-[#1A1A1A] text-white"
                      : isFree
                        ? "bg-[#FAFAFA] text-[#1A1A1A] border border-[#E5E5E0] hover:border-[#C5A059]"
                        : "bg-[#1A1A1A] text-white hover:bg-[#C5A059]"
                  }`}
                >
                  {activating === p.id ? (
                    <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Attivazione…</span>
                  ) : isCurrent ? (
                    "Piano attivo"
                  ) : (
                    "Inizia gratis"
                  )}
                </button>
              ) : (
                <TestPayPalButton
                  planId={p.id}
                  amount="1.00"
                  label={`Abbonati al piano ${p.label} · 1,00 € (test)`}
                  dark={isRecommended}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Current subscription management */}
      {currentPlan !== "free" && subscription && (
        <div className="mt-10 rounded-2xl border border-[#E5E5E0] bg-white p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="subscription-management">
          <div>
            <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium mb-1">Il tuo abbonamento</p>
            <p className="text-[#1A1A1A]">
              Piano <span className="font-semibold">{subscription.plan_label}</span>
              {subscription.billing_cycle && (
                <> • <span className="font-semibold">{subscription.billing_cycle === "yearly" ? "Annuale" : "Mensile"}</span></>
              )}
              {" "}• Stato:{" "}
              <span className="font-semibold">
                {subscription.status === "active"
                  ? "Attivo"
                  : subscription.status === "cancelled"
                    ? "Cancellato (attivo fino alla scadenza)"
                    : subscription.status}
              </span>
              {subscription.subscription_end && (
                <>
                  {" "}• Scadenza:{" "}
                  <span className="font-semibold">
                    {new Date(subscription.subscription_end).toLocaleDateString("it-IT")}
                  </span>
                </>
              )}
            </p>
          </div>
          {subscription.status === "active" && (
            <button
              onClick={cancel}
              className="h-11 px-5 rounded-xl border border-[#E5E5E0] text-[#1A1A1A] hover:border-[#C5A059] font-medium transition-all"
              data-testid="cancel-subscription-button"
            >
              Cancella abbonamento
            </button>
          )}
        </div>
      )}
    </Layout>
  );
}
